import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { Send, Shield, CheckCircle2, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const WHATSAPP_GROUP_URL = 'https://chat.whatsapp.com/Gr4Cx2ilhth2LjXFEaxf3E?s=cl&p=i&mlu=0&ilr=0';

const LeadForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [prefilledFromQuiz, setPrefilledFromQuiz] = useState(false);

  // Pré-preenche o form se o usuário acabou de vir do Quiz (sessionStorage).
  // Reduz fricção e aumenta taxa de conversão.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('wing_lead_prefill');
      if (!raw) return;
      const prefill = JSON.parse(raw);
      setFormData((prev) => ({
        ...prev,
        name: prefill.nome || prev.name,
        email: prefill.email || prev.email,
        phone: prefill.telefone || prev.phone,
        experience: prefill.experiencia || prev.experience,
        message: prefill.mensagem || prev.message,
      }));
      setPrefilledFromQuiz(true);
    } catch (err) {
      logger.warn('Falha ao ler pre-fill do sessionStorage:', err);
    }
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/leads`, {
        nome: formData.name,
        email: formData.email,
        telefone: formData.phone,
        experiencia: formData.experience,
        mensagem: formData.message,
        tipo_mentoria: 'mentoria_grupo',
        origem: 'site_mentoria_grupo',
        consentimento: true
      });

      logger.log('Lead salvo com sucesso:', response.data);

      toast({
        title: "Inscrição registrada! 🎉",
        description: "Entre no grupo do WhatsApp para acompanhar as novidades da turma.",
      });

      setFormData({ name: '', email: '', phone: '', experience: '', message: '' });

      // Mostra a tela de sucesso com o botão do grupo do WhatsApp
      setSubmitted(true);

      // Limpa o pre-fill do Quiz (já consumido)
      try { sessionStorage.removeItem('wing_lead_prefill'); } catch (_) { /* ignore */ }
    } catch (error) {
      logger.error('Erro ao salvar lead:', error);
      toast({
        title: "Erro ao processar inscrição",
        description: "Por favor, tente novamente ou entre em contato pelo WhatsApp.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="inscricao" className="section-padding bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Garanta sua <span className="gradient-text-green">Vaga</span>
            </h2>
            <p className="text-xl text-gray-400 mb-2">
              Cadastre seu interesse na próxima turma (início em breve). A <span className="text-white font-semibold">matrícula só é confirmada após uma entrevista individual</span> — sem compromisso agora.
            </p>
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-6 py-2 rounded-full mt-4">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
              <span className="text-yellow-400 font-semibold text-sm">4 aulas ao vivo + 20+ gravadas • Valor em breve • Vagas limitadas</span>
            </div>
            {prefilledFromQuiz && !submitted && (
              <div
                data-testid="lead-prefill-banner"
                className="mt-4 inline-flex items-center gap-2 bg-green-500/15 border border-green-500/40 text-green-300 text-xs sm:text-sm px-4 py-2 rounded-full"
              >
                <CheckCircle2 className="w-4 h-4" />
                Dados preenchidos automaticamente a partir do seu Quiz — só revise e confirme!
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
            {/* Left: Form OR Success state */}
            <div className="glass-dark p-6 md:p-8 rounded-2xl border border-green-500/20">
              {submitted ? (
                // === Success State: botão direto para o grupo (sem bloqueio de popup) ===
                <div className="text-center py-6" data-testid="lead-form-success">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center mb-5">
                    <CheckCircle2 className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
                    Inscrição registrada! 🎉
                  </h3>
                  <p className="text-gray-300 text-sm md:text-base mb-6 max-w-sm mx-auto">
                    Falta só um passo: <strong className="text-green-400">entre no grupo do WhatsApp</strong> para receber as novidades e garantir sua vaga na próxima turma.
                  </p>

                  <a
                    href={WHATSAPP_GROUP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-group-cta"
                    onClick={(e) => {
                      // Fallback robusto para iframes/popup-blockers
                      try {
                        const w = window.open(WHATSAPP_GROUP_URL, '_blank', 'noopener,noreferrer');
                        if (w) return;
                      } catch (err) {
                        logger.warn('window.open bloqueado:', err);
                      }
                      try {
                        if (window.top && window.top !== window.self) {
                          e.preventDefault();
                          window.top.location.href = WHATSAPP_GROUP_URL;
                          return;
                        }
                      } catch (err) {
                        logger.warn('Acesso a window.top bloqueado (cross-origin):', err);
                      }
                    }}
                    className="inline-flex items-center justify-center gap-3 w-full bg-green-500 hover:bg-green-600 text-white px-6 py-4 md:py-5 rounded-xl font-black text-base md:text-lg tracking-wide transition-all duration-300 hover:scale-[1.02] shadow-[0_20px_40px_-10px_rgba(16,185,129,0.5)]"
                  >
                    <MessageCircle className="w-6 h-6" />
                    ENTRE PARA GRUPO DE WHATSAPP
                  </a>

                  <p className="mt-3 text-xs text-gray-500">
                    Se não abrir automaticamente,{' '}
                    <a
                      href={WHATSAPP_GROUP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-yellow-400 underline"
                    >
                      clique aqui
                    </a>{' '}
                    para entrar no grupo.
                  </p>

                  <div>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-6 text-gray-400 hover:text-yellow-400 text-xs md:text-sm underline transition-colors"
                    >
                      ← Voltar e inscrever outra pessoa
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-white font-medium mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors duration-300"
                    placeholder="Seu nome completo"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-white font-medium mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors duration-300"
                    placeholder="seu@email.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-white font-medium mb-2">
                    WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors duration-300"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                {/* Experience */}
                <div>
                  <label htmlFor="experience" className="block text-white font-medium mb-2">
                    Qual seu Nível de Conhecimento sobre WING? <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                    data-testid="lead-experience-select"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500 transition-colors duration-300"
                  >
                    <option value="" className="bg-gray-900">Selecione uma opção</option>
                    <option value="iniciante" className="bg-gray-900">Iniciante - Nunca usei a WING</option>
                    <option value="basico" className="bg-gray-900">Básico - Já tive contato</option>
                    <option value="intermediario" className="bg-gray-900">Intermediário - Uso regularmente</option>
                    <option value="avancado" className="bg-gray-900">Avançado - Quero me especializar</option>
                  </select>
                </div>

                {/* Mensagem */}
                <div>
                  <label htmlFor="message" className="block text-white font-medium mb-2">
                    Deixe uma breve mensagem sobre você e sua carreira no áudio
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={2000}
                    placeholder="Conte um pouco sobre sua trajetória, em quais projetos atua, o que busca aprender..."
                    data-testid="lead-message-textarea"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors duration-300 resize-y"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !consent}
                  className="w-full bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-bold transition-all duration-300 hover:scale-105 glow-green flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      GARANTIR MINHA VAGA
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>

                {/* Security Note + LGPD consent */}
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="lead-consent-label">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      data-testid="lead-consent-checkbox"
                      className="mt-1 w-4 h-4 accent-green-500 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-400 leading-relaxed">
                      Li e concordo com a{' '}
                      <a
                        href="/politica-de-privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="lead-form-privacy-link"
                        className="text-yellow-400 hover:underline"
                      >
                        Política de Privacidade (LGPD)
                      </a>{' '}
                      e autorizo o contato da WING Academy Brasil por e-mail e WhatsApp.
                    </span>
                  </label>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Shield className="w-4 h-4 text-green-400" />
                    <span>Seus dados estão seguros e protegidos</span>
                  </div>
                </div>
              </form>
              )}
            </div>

            {/* Right: Benefits */}
            <div className="space-y-6">
              <div className="glass p-6 rounded-xl">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Sua inscrição inclui:
                </h3>
                <ul className="space-y-4">
                  {[
                    '4 aulas ao vivo com Adeilson Chagas',
                    'Mais de 20 aulas gravadas na plataforma',
                    'Acesso completo por 1 ano',
                    'Suporte direto para tirar dúvidas',
                    'Comunidade exclusiva de alunos',
                    'Certificado oficial WING Academy Brasil'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Guarantee */}
              <div className="bg-gradient-to-br from-green-500/10 to-transparent border border-green-500/20 p-6 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-lg mb-2">
                      Garantia de Satisfação
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Entrevista individual antes do pagamento para tirar todas as dúvidas + devolução de 100% em até 7 dias a partir da primeira aula ao vivo.
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="glass p-6 rounded-xl">
                <h4 className="text-white font-bold mb-3">Como funciona:</h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Você cadastra seu interesse (sem compromisso e sem pagamento)</li>
                  <li>• Entra no grupo do WhatsApp e passa por uma entrevista individual</li>
                  <li>• A matrícula é confirmada após a entrevista + pagamento</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;
